import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Upload, X, Check, Download, GripVertical } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { productsAPI, categoriesAPI, uploadAPI } from '../api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { ImportProductDialog } from './ImportProductDialog';
import { ProductGallery } from './ProductGallery';

export const AdminPanel = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imagePreviews, setImagePreviews] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    categoryId: '',
    category: '',
    price: '',
    description: '',
    images: [],
    featured: false,
    inStock: true,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [productsRes, categoriesRes] = await Promise.all([
        productsAPI.getAll(),
        categoriesAPI.getAll(),
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    try {
      setUploading(true);
      
      // Upload to backend
      const response = await uploadAPI.uploadImage(file);
      // Use full URL from backend for images
      const imageUrl = response.data.image_url.startsWith('http') 
        ? response.data.image_url 
        : `${process.env.REACT_APP_BACKEND_URL}${response.data.image_url}`;
      
      // Add to images array
      setFormData((prev) => ({
        ...prev,
        images: [...prev.images, imageUrl],
      }));
      
      // Add to previews
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews((prev) => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
      
      toast.success('Image uploaded successfully!');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.categoryId || !formData.price || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const selectedCategory = categories.find((c) => c.id === parseInt(formData.categoryId));
      const productData = {
        ...formData,
        categoryId: parseInt(formData.categoryId),
        category: selectedCategory?.name || '',
        price: parseFloat(formData.price),
      };

      if (editingProduct) {
        await productsAPI.update(editingProduct.id, productData);
        toast.success('Product updated successfully!');
      } else {
        await productsAPI.create(productData);
        toast.success('Product created successfully!');
      }

      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Failed to save product');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    const productImages = product.images || (product.image ? [product.image] : []);
    setFormData({
      name: product.name,
      categoryId: product.categoryId.toString(),
      category: product.category,
      price: product.price.toString(),
      description: product.description,
      images: productImages,
      featured: product.featured,
      inStock: product.inStock,
    });
    setImagePreviews(productImages);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      await productsAPI.delete(id);
      toast.success('Product deleted successfully!');
      fetchData();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      categoryId: '',
      category: '',
      price: '',
      description: '',
      images: [],
      featured: false,
      inStock: true,
    });
    setEditingProduct(null);
    setImagePreviews([]);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  const handleImportSuccess = async (importedProducts) => {
    // When products are imported, open the edit dialog with the first product
    if (importedProducts && importedProducts.length > 0) {
      const firstProduct = importedProducts[0];
      
      // If it's a single import, open edit dialog with data
      if (importedProducts.length === 1) {
        const productImages = firstProduct.image ? [firstProduct.image] : [];
        setFormData({
          name: firstProduct.name || '',
          categoryId: '',
          category: '',
          price: firstProduct.price?.toString() || '',
          description: firstProduct.description || '',
          images: productImages,
          featured: false,
          inStock: true,
        });
        setImagePreviews(productImages);
        setIsImportDialogOpen(false);
        setIsDialogOpen(true);
        toast.info('Review and complete the product details');
      } else {
        // Bulk import - create all products with default category
        const defaultCategory = categories[0];
        if (!defaultCategory) {
          toast.error('Please create at least one category first');
          return;
        }

        try {
          for (const prod of importedProducts) {
            const productImages = prod.image ? [prod.image] : [];
            const productData = {
              name: prod.name,
              categoryId: defaultCategory.id,
              category: defaultCategory.name,
              price: prod.price || 0,
              description: prod.description || '',
              images: productImages,
              featured: false,
              inStock: true,
            };
            await productsAPI.create(productData);
          }
          toast.success(`${importedProducts.length} products imported successfully!`);
          setIsImportDialogOpen(false);
          fetchData();
        } catch (error) {
          console.error('Error saving imported products:', error);
          toast.error('Failed to save some products');
        }
      }
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(products);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update local state immediately for smooth UX
    setProducts(items);

    // Update displayOrder for all products
    try {
      const updatePromises = items.map((product, index) =>
        productsAPI.update(product.id, { ...product, displayOrder: index })
      );
      await Promise.all(updatePromises);
      toast.success('Product order updated!');
    } catch (error) {
      console.error('Error updating product order:', error);
      toast.error('Failed to update order');
      // Revert on error
      fetchData();
    }
  };

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#f7f5f2] min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-[#1e1919]">Product Management</h1>
            <p className="text-[#736c64] mt-2">Manage your Thinkkool toys inventory</p>
          </div>
          <Button
            onClick={() => setIsDialogOpen(true)}
            className="bg-[#61525a] hover:bg-[#4a3f45] text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Product
          </Button>
          <Button
            onClick={() => setIsImportDialogOpen(true)}
            variant="outline"
            className="border-[#61525a] text-[#61525a] hover:bg-[#61525a] hover:text-white"
          >
            <Download className="w-4 h-4 mr-2" />
            Import from Amazon
          </Button>
        </div>

        {/* Products Grid with Drag & Drop */}
        {loading ? (
          <div className="text-center py-20">
            <div className="text-lg text-[#736c64]">Loading products...</div>
          </div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable 
              droppableId="products" 
              isDropDisabled={false}
              isCombineEnabled={false}
              ignoreContainerClipping={false}
            >
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                >
                  {products.map((product, index) => (
                    <Draggable key={product.id} draggableId={product.id} index={index}>
                      {(provided, snapshot) => (
                        <Card
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`overflow-hidden border-2 border-[#61525a]/20 ${
                            snapshot.isDragging ? 'shadow-2xl ring-2 ring-[#61525a]' : ''
                          }`}
                        >
                          <CardContent className="p-0">
                            {/* Drag Handle */}
                            <div
                              {...provided.dragHandleProps}
                              className="bg-[#61525a] text-white px-4 py-2 flex items-center gap-2 cursor-grab active:cursor-grabbing"
                            >
                              <GripVertical className="w-4 h-4" />
                              <span className="text-xs font-medium">Drag to reorder</span>
                              <Badge className="ml-auto bg-white/20 text-white border-0">
                                #{index + 1}
                              </Badge>
                            </div>

                            {/* Product Image Gallery */}
                            <div className="relative bg-white">
                              <ProductGallery
                                images={product.images || (product.image ? [product.image] : [])}
                                productName={product.name}
                              />
                              {product.featured && (
                                <Badge className="absolute top-3 right-3 bg-[#ff8c19] text-white border-0 z-10">
                                  Featured
                                </Badge>
                              )}
                            </div>

                            {/* Product Info */}
                            <div className="p-4 space-y-3">
                              <div>
                                <Badge variant="outline" className="mb-2 text-xs">
                                  {product.category}
                                </Badge>
                                <h3 className="text-lg font-bold text-[#1e1919]">{product.name}</h3>
                              </div>

                              <p className="text-sm text-[#736c64] line-clamp-2">{product.description}</p>

                              {/* Action Buttons */}
                              <div className="flex gap-2 pt-2">
                                <Button
                                  onClick={() => handleEdit(product)}
                                  variant="outline"
                                  size="sm"
                                  className="flex-1"
                                >
                                  <Edit className="w-4 h-4 mr-1" />
                                  Edit
                                </Button>
                                <Button
                                  onClick={() => handleDelete(product.id)}
                                  variant="destructive"
                                  size="sm"
                                  className="flex-1"
                                >
                                  <Trash2 className="w-4 h-4 mr-1" />
                                  Delete
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}

        {/* Add/Edit Product Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={handleDialogClose}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Image Upload */}
              <div className="space-y-2">
                <Label>Product Images *</Label>
                <div className="flex flex-col gap-4">
                  {/* Image Previews Grid */}
                  {imagePreviews.length > 0 && (
                    <div className="grid grid-cols-3 gap-3">
                      {imagePreviews.map((preview, index) => (
                        <div
                          key={index}
                          className="relative aspect-square rounded-lg overflow-hidden border-2 border-[#61525a]/20"
                        >
                          <img
                            src={preview}
                            alt={`Preview ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveImage(index)}
                            className="absolute top-1 right-1 bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 transition-colors"
                          >
                            <X className="w-3 h-3" />
                          </button>
                          {index === 0 && (
                            <Badge className="absolute bottom-1 left-1 bg-[#61525a] text-white text-xs">
                              Primary
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Upload Button */}
                  <div>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploading}
                      className="cursor-pointer"
                    />
                    <p className="text-xs text-[#736c64] mt-1">
                      Max file size: 5MB. Supported formats: JPEG, PNG, WebP. You can add multiple images.
                    </p>
                  </div>
                </div>
              </div>

              {/* Product Name */}
              <div className="space-y-2">
                <Label htmlFor="name">Product Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Rainbow Pop Fidget"
                  required
                />
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <Select
                  value={formData.categoryId}
                  onValueChange={(value) => setFormData({ ...formData, categoryId: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id.toString()}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Price */}
              <div className="space-y-2">
                <Label htmlFor="price">Price ($) *</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="9.99"
                  required
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the product..."
                  rows={3}
                  required
                />
              </div>

              {/* Featured Toggle */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <Label>Featured Product</Label>
                  <p className="text-sm text-[#736c64]">Show this product in featured section</p>
                </div>
                <Switch
                  checked={formData.featured}
                  onCheckedChange={(checked) => setFormData({ ...formData, featured: checked })}
                />
              </div>

              {/* In Stock Toggle */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <Label>In Stock</Label>
                  <p className="text-sm text-[#736c64]">Product availability status</p>
                </div>
                <Switch
                  checked={formData.inStock}
                  onCheckedChange={(checked) => setFormData({ ...formData, inStock: checked })}
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleDialogClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-[#61525a] hover:bg-[#4a3f45]"
                  disabled={uploading || formData.images.length === 0}
                >
                  {uploading ? (
                    'Uploading...'
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      {editingProduct ? 'Update Product' : 'Create Product'}
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Import Product Dialog */}
        <ImportProductDialog
          isOpen={isImportDialogOpen}
          onClose={() => setIsImportDialogOpen(false)}
          onImportSuccess={handleImportSuccess}
        />
      </div>
    </section>
  );
};
